import io from 'socket.io-client';

const state = {
    socket: null,
    currentOrderRoom: null,
    isConnected: false,
    loading: false,
    error: null,
    // Order sync state
    orderData: null,
    scanProgress: {},
    orderStatus: null,
    printStatus: {
        isPrintable: false,
        isPrinting: false,
        isPrinted: false
    },
    packingStatus: {
        isPackable: false,
        isPacked: false
    },
    mobileWorkflowState: null, // Mobile workflow state: { step, pickingScannedArticles, packingScannedArticles }
    lastUpdated: null
};

const getters = {
    isConnected: state => state.isConnected,
    currentOrderRoom: state => state.currentOrderRoom,
    orderData: state => state.orderData,
    scanProgress: state => state.scanProgress,
    orderStatus: state => state.orderStatus,
    printStatus: state => state.printStatus,
    packingStatus: state => state.packingStatus,
    mobileWorkflowState: state => state.mobileWorkflowState,
    lastUpdated: state => state.lastUpdated,
    hasError: state => state.error
};

const actions = {
    // Initialize socket connection for orders
    initSocket({ commit, dispatch }, user) {
        // Extract user information
        const userId = user?.user || user?.id || user?._id || localStorage.getItem('userId') || sessionStorage.getItem('userId');
        const username = user?.name || user?.username || 'Unknown';
        const userRole = user?.role || 'user';
        
        console.log('[OrderSync] Attempting to connect to /orders namespace');
        console.log('[OrderSync] With user data:', { userId, username, userRole });
        
        // Connect to orders namespace
        const socket = io('/orders', {
            auth: {
                userId: userId,
                username: username,
                role: userRole
            },
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
            timeout: 20000,
            transports: ['polling', 'websocket']
        });

        // Connection event handlers
        socket.on('connect', () => {
            console.log('[OrderSync] ✅ Connected to orders namespace');
            console.log('[OrderSync] Socket ID:', socket.id);
            commit('SET_CONNECTED', true);
            commit('SET_ERROR', null);
        });
        
        socket.on('connect_error', (error) => {
            console.error('[OrderSync] ❌ Connection error:', error.message);
            commit('SET_CONNECTED', false);
            commit('SET_ERROR', `Connection failed: ${error.message}`);
        });
        
        socket.on('disconnect', (reason) => {
            console.log('[OrderSync] ⚠️ Disconnected:', reason);
            commit('SET_CONNECTED', false);
            commit('SET_CURRENT_ORDER_ROOM', null);
        });
        
        socket.on('reconnect', (attemptNumber) => {
            console.log('[OrderSync] 🔄 Reconnected after', attemptNumber, 'attempts');
            // Rejoin order room if we had one
            if (state.currentOrderRoom) {
                dispatch('joinOrderRoom', state.currentOrderRoom);
            }
        });

        // Order-specific event handlers
        socket.on('order-updated', (data) => {
            // If this order update includes scan progress, update our store
            if (data.order?.scanProgress) {
                const scanProgressCount = Object.keys(data.order.scanProgress).length;
                console.log(`[OrderSync] Restoring scan progress: ${scanProgressCount} items`);
                
                Object.keys(data.order.scanProgress).forEach(itemId => {
                    const progress = data.order.scanProgress[itemId];
                    commit('UPDATE_ITEM_SCAN_STATUS', {
                        itemId,
                        scanned: progress.scanned,
                        scannedCount: progress.scannedCount,
                        totalQuantity: progress.totalQuantity
                    });
                });
            }
            
            commit('SET_ORDER_DATA', data.order);
            commit('SET_LAST_UPDATED', new Date());
        });

        socket.on('scan-progress-updated', (data) => {
            console.log('[OrderSync] 🔍 Scan progress updated:', data);
            commit('UPDATE_SCAN_PROGRESS', data);
            commit('SET_LAST_UPDATED', new Date());
        });

        socket.on('print-status-updated', (data) => {
            console.log('[OrderSync] 🖨️ Print status updated:', data);
            commit('SET_PRINT_STATUS', data.printStatus);
            commit('SET_LAST_UPDATED', new Date());
        });

        socket.on('pack-status-updated', (data) => {
            console.log('[OrderSync] 📦 Pack status updated:', data);
            commit('SET_PACKING_STATUS', data.packingStatus);
            commit('SET_LAST_UPDATED', new Date());
        });

        socket.on('order-status-changed', (data) => {
            console.log('[OrderSync] 🔄 Order status changed:', data);
            commit('SET_ORDER_STATUS', data.status);
            commit('SET_LAST_UPDATED', new Date());
        });

        socket.on('mobile-workflow-updated', (data) => {
            console.log('[OrderSync] 📱 Mobile workflow updated event received:', {
                orderId: data.orderId,
                step: data.workflowState?.step,
                pickingScans: Object.keys(data.workflowState?.pickingScannedArticles || {}).length,
                packingScans: Object.keys(data.workflowState?.packingScannedArticles || {}).length,
                updatedBy: data.updatedBy,
                isRestoredState: data.isRestoredState
            });
            commit('SET_MOBILE_WORKFLOW_STATE', data.workflowState);
            commit('SET_LAST_UPDATED', new Date());
            console.log('[OrderSync] ✅ Mobile workflow state committed to store');
        });

        socket.on('item-scanned', (data) => {
            const isRestored = data.isRestoredState;
            console.log(`[OrderSync] Item scanned ${isRestored ? '(restored)' : '(live)'}: ${data.orderId}/${data.itemId}`);
            
            commit('UPDATE_ITEM_SCAN_STATUS', {
                itemId: data.itemId,
                scanned: data.scannedData?.isFullyScanned || false,
                scannedCount: data.scannedData?.scannedCount || 1,
                totalQuantity: data.scannedData?.totalQuantity || 1,
                isRestored
            });
            commit('SET_LAST_UPDATED', new Date());
        });

        socket.on('item-reset', (data) => {
            console.log('[OrderSync] 🔄 Item reset from other device:', data);
            commit('RESET_ITEM_SCAN_STATUS', { itemId: data.itemId });
            commit('SET_LAST_UPDATED', new Date());
        });

        // Room management events
        socket.on('joined-order-room', (data) => {
            console.log('[OrderSync] 🏠 Joined order room:', data.orderId);
            commit('SET_CURRENT_ORDER_ROOM', data.orderId);
            // Request current order state
            dispatch('requestOrderState', data.orderId);
        });

        socket.on('left-order-room', (data) => {
            console.log('[OrderSync] 🚪 Left order room:', data.orderId);
            if (state.currentOrderRoom === data.orderId) {
                commit('SET_CURRENT_ORDER_ROOM', null);
                commit('CLEAR_ORDER_DATA');
            }
        });

        socket.on('order-room-error', (error) => {
            console.error('[OrderSync] 🚨 Order room error:', error);
            commit('SET_ERROR', error.message);
        });

        commit('SET_SOCKET', socket);
        console.log('[OrderSync] Socket instance stored in Vuex');
    },

    // Join specific order room
    joinOrderRoom({ state, commit, dispatch }, orderId) {
        console.log('[OrderSync Store] joinOrderRoom called', {
            orderId,
            hasSocket: !!state.socket,
            isConnected: state.isConnected,
            currentRoom: state.currentOrderRoom,
            socketId: state.socket?.id
        });

        if (!state.socket) {
            console.error('[OrderSync] ❌ Cannot join room: socket not initialized');
            return;
        }

        if (!state.isConnected) {
            console.warn('[OrderSync] ⚠️ Socket not yet connected, waiting...');
            // Wait for connection and retry
            const checkConnection = setInterval(() => {
                if (state.isConnected) {
                    clearInterval(checkConnection);
                    console.log('[OrderSync] ✅ Socket now connected, retrying join');
                    dispatch('joinOrderRoom', orderId);
                }
            }, 100);

            // Timeout after 5 seconds
            setTimeout(() => {
                clearInterval(checkConnection);
                if (!state.isConnected) {
                    console.error('[OrderSync] ❌ Timeout waiting for socket connection');
                }
            }, 5000);
            return;
        }

        if (state.currentOrderRoom === orderId) {
            console.log('[OrderSync] ⚠️ Already in order room:', orderId);
            return;
        }

        console.log('[OrderSync] ✅ Emitting join-order-room for:', orderId);
        state.socket.emit('join-order-room', { orderId });
        console.log('[OrderSync] ✅ join-order-room emitted successfully');
    },

    // Leave current order room
    leaveOrderRoom({ state, commit }) {
        if (!state.socket || !state.currentOrderRoom) return;

        console.log('[OrderSync] Leaving order room:', state.currentOrderRoom);
        state.socket.emit('leave-order-room', { orderId: state.currentOrderRoom });
        commit('SET_CURRENT_ORDER_ROOM', null);
        commit('CLEAR_ORDER_DATA');
    },

    // Request current order state
    requestOrderState({ state }, orderId) {
        if (!state.socket || !state.isConnected) return;

        console.log('[OrderSync] Requesting order state:', orderId);
        state.socket.emit('get-order-state', { orderId });
    },

    // Emit scan item event
    emitScanItem({ state }, { orderId, itemId, scannedData }) {
        if (!state.socket || !state.isConnected) return;

        console.log('[OrderSync] Emitting scan item:', { orderId, itemId });
        state.socket.emit('scan-item', { 
            orderId, 
            itemId, 
            scannedData,
            timestamp: new Date()
        });
    },

    // Emit item reset event
    emitItemReset({ state }, { orderId, itemId }) {
        if (!state.socket || !state.isConnected) return;

        console.log('[OrderSync] Emitting item reset:', { orderId, itemId });
        state.socket.emit('reset-item', { 
            orderId, 
            itemId,
            timestamp: new Date()
        });
    },

    // Emit print status update
    emitPrintStatusUpdate({ state }, { orderId, printStatus }) {
        if (!state.socket || !state.isConnected) return;

        console.log('[OrderSync] Emitting print status:', { orderId, printStatus });
        state.socket.emit('update-print-status', { 
            orderId, 
            printStatus,
            timestamp: new Date()
        });
    },

    // Emit pack status update
    emitPackStatusUpdate({ state }, { orderId, packingStatus }) {
        if (!state.socket || !state.isConnected) return;

        console.log('[OrderSync] Emitting pack status:', { orderId, packingStatus });
        state.socket.emit('update-pack-status', { 
            orderId, 
            packingStatus,
            timestamp: new Date()
        });
    },

    // Emit order status change
    emitOrderStatusChange({ state }, { orderId, status }) {
        if (!state.socket || !state.isConnected) return;

        console.log('[OrderSync] Emitting order status change:', { orderId, status });
        state.socket.emit('update-order-status', { 
            orderId, 
            status,
            timestamp: new Date()
        });
    },

    // Emit order packed event to sync all devices
    emitOrderPacked({ state }, { orderId }) {
        if (!state.socket || !state.isConnected) return;

        console.log('[OrderSync] Emitting order packed event:', { orderId });
        state.socket.emit('update-order-status', {
            orderId,
            status: 'order-packed',
            timestamp: new Date()
        });
    },

    // Emit mobile workflow state update
    emitMobileWorkflowUpdate({ state }, { orderId, workflowState }) {
        console.log('[OrderSync Store] emitMobileWorkflowUpdate called', {
            hasSocket: !!state.socket,
            isConnected: state.isConnected,
            currentRoom: state.currentOrderRoom,
            orderId,
            step: workflowState.step
        });

        if (!state.socket || !state.isConnected) {
            console.error('[OrderSync Store] ❌ Cannot emit - socket not connected');
            return;
        }

        console.log('[OrderSync Store] ✅ Emitting mobile workflow update via socket.emit');
        state.socket.emit('update-mobile-workflow', {
            orderId,
            workflowState,
            timestamp: new Date()
        });
        console.log('[OrderSync Store] ✅ Socket.emit called successfully');
    },

    // Cleanup socket
    cleanup({ state, commit }) {
        if (state.socket) {
            console.log('[OrderSync] Cleaning up socket connection');
            // Leave current room before disconnecting
            if (state.currentOrderRoom) {
                state.socket.emit('leave-order-room', { orderId: state.currentOrderRoom });
            }
            state.socket.disconnect();
            commit('SET_SOCKET', null);
            commit('SET_CONNECTED', false);
            commit('SET_CURRENT_ORDER_ROOM', null);
        }
    }
};

const mutations = {
    SET_SOCKET(state, socket) {
        state.socket = socket;
    },
    
    SET_CONNECTED(state, connected) {
        state.isConnected = connected;
    },
    
    SET_CURRENT_ORDER_ROOM(state, orderId) {
        state.currentOrderRoom = orderId;
    },
    
    SET_ERROR(state, error) {
        state.error = error;
    },
    
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    
    SET_ORDER_DATA(state, orderData) {
        state.orderData = orderData;
    },
    
    SET_ORDER_STATUS(state, status) {
        state.orderStatus = status;
    },
    
    UPDATE_SCAN_PROGRESS(state, { itemId, progress, scannedCount, totalCount }) {
        if (!state.scanProgress) {
            state.scanProgress = {};
        }
        state.scanProgress[itemId] = { progress, scannedCount, totalCount };
    },
    
    UPDATE_ITEM_SCAN_STATUS(state, { itemId, scanned, scannedCount }) {
        if (!state.scanProgress) {
            state.scanProgress = {};
        }
        if (!state.scanProgress[itemId]) {
            state.scanProgress[itemId] = {};
        }
        
        state.scanProgress[itemId].scanned = scanned;
        state.scanProgress[itemId].scannedCount = scannedCount;
        
        // Force reactivity by creating new object reference
        state.scanProgress = { ...state.scanProgress };
    },
    
    RESET_ITEM_SCAN_STATUS(state, { itemId }) {
        if (state.scanProgress && state.scanProgress[itemId]) {
            state.scanProgress[itemId].scanned = false;
            state.scanProgress[itemId].scannedCount = 0;
            
            // Force reactivity by creating new object reference
            state.scanProgress = { ...state.scanProgress };
        }
    },
    
    SET_PRINT_STATUS(state, printStatus) {
        state.printStatus = { ...state.printStatus, ...printStatus };
    },
    
    SET_PACKING_STATUS(state, packingStatus) {
        state.packingStatus = { ...state.packingStatus, ...packingStatus };
    },

    SET_MOBILE_WORKFLOW_STATE(state, workflowState) {
        state.mobileWorkflowState = workflowState;
    },

    SET_LAST_UPDATED(state, timestamp) {
        state.lastUpdated = timestamp;
    },

    CLEAR_ORDER_DATA(state) {
        state.orderData = null;
        state.scanProgress = {};
        state.orderStatus = null;
        state.printStatus = {
            isPrintable: false,
            isPrinting: false,
            isPrinted: false
        };
        state.packingStatus = {
            isPackable: false,
            isPacked: false
        };
        state.mobileWorkflowState = null;
        state.lastUpdated = null;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
